import { apiClient } from '@/shared/api/apiClient';

// Sheet images that live in the document center are stored in the grid as a
// `quadratic-document:<uuid>` reference (see parseDocumentImageUri). The bytes
// are fetched through a presigned download URL, which is short-lived and so
// can't itself be stored in the grid — this module does that resolution at
// render time, which is also where the viewer's permission on the document is
// enforced.
//
// Document contents are not immutable for a given uuid: a content edit
// (update-complete) swaps the stored object in place and bumps `updatedDate`.
// So every resolution fetches the document metadata, and only the downloaded
// bytes are reused — and only while `updatedDate` is unchanged. Superseded
// object URLs are deliberately not revoked: sprites that already rendered the
// old bytes keep their URL alive until they re-resolve.
const blobCache = new Map<string, { updatedDate: string; url: string }>();
const inFlight = new Map<string, Promise<string>>();

const fetchDocumentImage = async (documentUuid: string): Promise<string> => {
  const { document } = await apiClient.documents.get(documentUuid);

  const cached = blobCache.get(documentUuid);
  if (cached && cached.updatedDate === document.updatedDate) return cached.url;

  const response = await fetch(document.downloadUrl);
  if (!response.ok) {
    throw new Error(`Failed to download document image ${documentUuid}: ${response.status}`);
  }
  const url = URL.createObjectURL(await response.blob());
  blobCache.set(documentUuid, { updatedDate: document.updatedDate, url });
  return url;
};

/**
 * Resolves a document-center image to a URL the grid renderer can load.
 * Rejects when the document has been deleted or the viewer can't access it.
 */
export const resolveDocumentImageUrl = (documentUuid: string): Promise<string> => {
  const pending = inFlight.get(documentUuid);
  if (pending) return pending;

  const promise = fetchDocumentImage(documentUuid);
  // Concurrent callers share one request; failures aren't cached, so a network
  // blip or late-granted access resolves on a later render.
  const cleanup = () => {
    if (inFlight.get(documentUuid) === promise) inFlight.delete(documentUuid);
  };
  promise.then(cleanup, cleanup);
  inFlight.set(documentUuid, promise);
  return promise;
};

/** Natural pixel dimensions of a document image, used to size its cell span. */
export const getDocumentImageDimensions = (url: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => reject(new Error('Failed to decode document image'));
    img.src = url;
  });
